import type {
  AssignableOrganizationRole,
  Organization,
  OrganizationCreateParams,
  OrganizationInvitation,
  OrganizationInvitationCreateParams,
  OrganizationMember,
  OrganizationSettings,
  OrganizationSettingsPatchParams,
  OrganizationUpdateParams,
  PublishableKey,
  PublishableKeyCreateParams,
  PublishableKeyUpdateParams,
} from "@mountaya/client";

import type { RestClientConfig } from "../../rest/rules/sdk";
import type { components } from "../../schema/api";
import {
  invitationsFor,
  membersFor,
  organizationsFor,
  ownershipFor,
  publishableKeysFor,
  secretKeysFor,
} from "./sdk";

/**
 * The secret-key vocabulary is reached through `components["schemas"]` rather than imported,
 * because it is the half of this module the contract does not publish, the same line the
 * seams in ./sdk draw, spelled in types. An expression of that shape says "not on the public
 * contract" by its own form, which is why the console types keep it and the published ones
 * above are plain imports.
 */
type SecretKey = components["schemas"]["SecretKey"];
type SecretKeyCreateParams = components["schemas"]["SecretKeyCreateParams"];
type SecretKeyUpdateParams = components["schemas"]["SecretKeyUpdateParams"];

/**
 * Every body below is built member by member from the params, never passed through whole. The
 * API refuses a request body carrying a member its contract does not declare (a 400 naming it),
 * and a params value typed narrowly can still hold more at runtime: a form state, a fetched
 * resource spread into an edit. Picking the declared members is what keeps a stray one off the
 * wire, and a member left undefined is dropped by the JSON encoding, which reads as "leave it".
 */

/**
 * createOrganization creates a new organization owned by the user, returning the
 * created organization the backend echoes so the caller can navigate to it without a
 * refetch. A non-ok response throws the transport's typed error: the name-length 400
 * arrives as a MountayaBadRequestError carrying the backend's localized message and its
 * path, which is what the create form surfaces on the field.
 *
 * `idempotencyKey` is the `Idempotency-Key` the create is sent under, when the caller holds one
 * across its own resubmits: a resubmit after an answer that was lost then repeats the same
 * request, and is told the organization was made (409 `IDEMPOTENCY_ANSWER_UNAVAILABLE`, naming
 * it) rather than making a second one. Left out, the client draws a key for the call.
 */
export const createOrganization = async (
  config: RestClientConfig,
  params: OrganizationCreateParams,
  idempotencyKey?: string,
): Promise<Organization> => {
  const echoed = await organizationsFor(config).postOrganization({
    body: {
      agreement_requested: params.agreement_requested,
      name: params.name,
      website: params.website,
    },
    idempotencyKey,
  });

  return echoed.data;
};

/**
 * updateOrganization renames an organization, returning the updated organization the backend
 * echoes. `ifMatch` is the `etag` the organization was read under: a rename made over a change
 * somebody else made since is refused with a 412 (`MountayaPreconditionFailedError`) and changes
 * nothing, so the caller reloads the organization and the person decides again. A non-ok
 * response throws a typed error.
 */
export const updateOrganization = async (
  config: RestClientConfig,
  organizationId: string,
  params: OrganizationUpdateParams,
  ifMatch: string | undefined,
): Promise<Organization> => {
  const echoed = await organizationsFor(config).patchOrganization({
    body: { name: params.name },
    ifMatch,
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * updateOrganizationSettings applies an owner/admin partial update of the
 * organization's self-serve settings — any subset of `default_props`, `theme`,
 * and `safety_rules` — and returns the settings the backend echoes back, whole:
 * the groups that were replaced and the groups that were left alone. Only the
 * groups present in the request are replaced.
 *
 * `ifMatch` is the settings' own `etag`, apart from the organization's: an edit made over a
 * settings change somebody else made since is refused with a 412 and writes nothing, where it
 * would otherwise have silently put back the groups it re-sent. A rename never refuses it.
 *
 * It answers with the SETTINGS rather than the organization because that is the
 * resource it wrote, which is what every other write in the contract does. The
 * per-minute rate limits are not settings: they are limits of the organization's
 * agreement, which staff set on its entitlements, and a caller that has exhausted one
 * learns it from a 429. A non-ok response throws a typed error.
 */
export const updateOrganizationSettings = async (
  config: RestClientConfig,
  organizationId: string,
  params: OrganizationSettingsPatchParams,
  ifMatch: string | undefined,
): Promise<OrganizationSettings> => {
  const echoed = await organizationsFor(config).patchOrganizationSettings({
    body: {
      default_props: params.default_props,
      safety_rules: params.safety_rules,
      theme: params.theme,
    },
    ifMatch,
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * createOrganizationInvitation invites one address to an organization with the role the person
 * holds once they accept, and returns the invitation as made. A form inviting several people
 * calls it once per address: the API makes one invitation per request.
 *
 * Every well-formed address is answered alike whether or not an account carries it. A non-ok
 * response throws a typed error, which the form reads per address: a 409 when the address
 * already belongs to a member or already has an invitation waiting (its metadata names which), a
 * 402 coded `LIMIT_REACHED` at `seats`, a 403 coded `LIMIT_REACHED` at `pending-invitations`, and
 * a 429 carrying how long to wait before inviting again.
 */
export const createOrganizationInvitation = async (
  config: RestClientConfig,
  organizationId: string,
  params: OrganizationInvitationCreateParams,
): Promise<OrganizationInvitation> => {
  const created = await invitationsFor(config).postOrganizationInvitation({
    body: { email: params.email, role: params.role },
    organization_id: organizationId,
  });

  return created.data;
};

/**
 * revokeOrganizationInvitation withdraws an invitation still waiting for an answer, which frees
 * the seat it held. Withdrawing one already gone is not an error, so a double click or a race with
 * the person's own answer resolves alike. Resolves to void; a non-ok response throws a typed
 * error.
 */
export const revokeOrganizationInvitation = async (
  config: RestClientConfig,
  organizationId: string,
  invitationId: string,
): Promise<void> => {
  await invitationsFor(config).deleteOrganizationInvitation({
    invitation_id: invitationId,
    organization_id: organizationId,
  });
};

/**
 * updateOrganizationMemberRole changes one member's role. The backend echoes the
 * updated member, returned so the caller can refresh the row without a refetch. Only the owner
 * grants `admin` or changes an admin's role, and nobody changes the owner's, which
 * `assignableRolesFor` mirrors so the page never offers a change the API would refuse. A non-ok
 * response throws a typed error.
 */
export const updateOrganizationMemberRole = async (
  config: RestClientConfig,
  organizationId: string,
  userId: string,
  role: AssignableOrganizationRole,
): Promise<OrganizationMember> => {
  const echoed = await membersFor(config).patchOrganizationMember({
    body: { role },
    organization_id: organizationId,
    user_id: userId,
  });

  return echoed.data;
};

/**
 * removeOrganizationMember removes one member from an organization, or, when the member is the
 * signed-in user, leaves it. The member's seat is freed and what they created stays with the
 * organization. Removing somebody who has already gone answers alike, so a removal sent twice is
 * not an error. Resolves to void; a non-ok response throws a typed error (the 409 for the owner,
 * who transfers ownership before leaving).
 */
export const removeOrganizationMember = async (
  config: RestClientConfig,
  organizationId: string,
  userId: string,
): Promise<void> => {
  await membersFor(config).deleteOrganizationMember({
    organization_id: organizationId,
    user_id: userId,
  });
};

/**
 * transferOrganizationOwnership makes another member the organization's owner and the signed-in
 * owner an admin, in one change, and returns the new owner. Only the owner sends it, from this
 * console: the operation is the console's ({@link ownershipFor}), answered to a console session
 * alone. A non-ok response throws a typed error: a 404 for somebody who has not joined (a pending
 * invitee included), a 403 for anybody but the owner, and for any credential but a console
 * session.
 */
export const transferOrganizationOwnership = async (
  config: RestClientConfig,
  organizationId: string,
  userId: string,
): Promise<OrganizationMember> => {
  const echoed = await ownershipFor(config).postOrganizationTransferOwnership({
    body: { user_id: userId },
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * createPublishableKey creates a publishable key (name + scopes), returning the
 * created key the backend echoes (its `key` value is readable on every read for
 * publishable keys). A non-ok response throws a typed error.
 *
 * `idempotencyKey` is the `Idempotency-Key` the create is sent under, when the caller holds one
 * across its own repeats: a repeat of a create still being made (409 `IDEMPOTENCY_KEY_IN_USE`),
 * or of one whose answer never arrived, is then the same request, which the API answers with the
 * first one's answer rather than making a second key. Left out, the client draws a key for the
 * call.
 */
export const createPublishableKey = async (
  config: RestClientConfig,
  organizationId: string,
  params: PublishableKeyCreateParams,
  idempotencyKey?: string,
): Promise<PublishableKey> => {
  const echoed = await publishableKeysFor(config).postOrganizationPublishableKey({
    body: { name: params.name, scopes: params.scopes },
    idempotencyKey,
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * updatePublishableKey updates a publishable key's name and/or scopes, returning the updated key
 * the backend echoes. `ifMatch` is the `etag` the key was read under: an edit made over a change
 * somebody else made since is refused with a 412 and writes nothing. A non-ok response throws a
 * typed error.
 */
export const updatePublishableKey = async (
  config: RestClientConfig,
  organizationId: string,
  keyId: string,
  params: PublishableKeyUpdateParams,
  ifMatch: string | undefined,
): Promise<PublishableKey> => {
  const echoed = await publishableKeysFor(config).patchOrganizationPublishableKey({
    body: { name: params.name, scopes: params.scopes },
    ifMatch,
    key_id: keyId,
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * deletePublishableKey revokes a publishable key, and returns the key the API answers with:
 * revoked, by this request or an earlier one, carrying `revoked_at` and its new `etag`. `ifMatch`
 * is the `etag` the key was read under, so a revoke confirmed against a key somebody has since
 * changed is refused with a 412 rather than revoking what the confirm did not show. A non-ok
 * response throws a typed error.
 */
export const deletePublishableKey = async (
  config: RestClientConfig,
  organizationId: string,
  keyId: string,
  ifMatch: string | undefined,
): Promise<PublishableKey> => {
  const revoked = await publishableKeysFor(config).deleteOrganizationPublishableKey({
    ifMatch,
    key_id: keyId,
    organization_id: organizationId,
  });

  return revoked.data;
};

/**
 * createSecretKey creates a secret key (name only), returning the created key the
 * backend echoes. The echoed key carries its secret `key` value — the only time
 * the backend ever returns it — so the caller must surface it once and never
 * refetch it. A non-ok response throws a typed error.
 */
export const createSecretKey = async (
  config: RestClientConfig,
  organizationId: string,
  params: SecretKeyCreateParams,
): Promise<SecretKey> => {
  const echoed = await secretKeysFor(config).postOrganizationSecretKey({
    body: { name: params.name },
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * updateSecretKey renames a secret key, returning the updated key the backend
 * echoes (without the secret value — that is creation-only). A non-ok response
 * throws a typed error.
 */
export const updateSecretKey = async (
  config: RestClientConfig,
  organizationId: string,
  keyId: string,
  params: SecretKeyUpdateParams,
): Promise<SecretKey> => {
  const echoed = await secretKeysFor(config).patchOrganizationSecretKey({
    body: { name: params.name },
    key_id: keyId,
    organization_id: organizationId,
  });

  return echoed.data;
};

/**
 * deleteSecretKey revokes a secret key, and returns the key the API answers with: revoked, by
 * this request or an earlier one, carrying `revoked_at` and never its value. Revoking one already
 * revoked answers alike, so a second confirm is not an error. A non-ok response throws a typed
 * error. The revoke confirmation notes that already-issued session tokens signed by the key stay
 * valid until they expire.
 */
export const deleteSecretKey = async (
  config: RestClientConfig,
  organizationId: string,
  keyId: string,
): Promise<SecretKey> => {
  const revoked = await secretKeysFor(config).deleteOrganizationSecretKey({
    key_id: keyId,
    organization_id: organizationId,
  });

  return revoked.data;
};
